import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Home, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SaveCalculationDialog = ({ 
  open, 
  onOpenChange, 
  calculatorType, // "mortgage" or "affordability"
  calculationData,
  onSaved
}) => {
  const [name, setName] = useState('');
  const [selectedListing, setSelectedListing] = useState('none');
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch active listings when dialog opens
  useEffect(() => {
    if (open) {
      fetchListings();
      // Generate default name based on calculator type
      const defaultName = calculatorType === 'mortgage' 
        ? `Mortgage - $${(calculationData?.home_price || 0).toLocaleString()}`
        : `Affordability - $${(calculationData?.max_home_price || 0).toLocaleString()}`;
      setName(defaultName);
    }
  }, [open, calculatorType, calculationData]);

  const fetchListings = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to only active listings (case-insensitive check)
        const activeListings = data.filter(l => 
          l.status?.toLowerCase() === 'active'
        );
        setListings(activeListings);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name for this calculation');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        calculator_type: calculatorType,
        name: name.trim(),
        listing_id: selectedListing === 'none' ? null : selectedListing,
        mortgage_data: calculatorType === 'mortgage' ? calculationData : null,
        affordability_data: calculatorType === 'affordability' ? calculationData : null
      };

      const response = await fetch(`${API_URL}/api/calculations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const saved = await response.json();
        toast.success(
          selectedListing !== 'none' 
            ? 'Calculation saved and assigned to listing!' 
            : 'Calculation saved!'
        );
        onOpenChange(false);
        if (onSaved) onSaved(saved);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save calculation');
      }
    } catch (error) {
      console.error('Error saving calculation:', error);
      toast.error('Failed to save calculation');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save size={20} className="text-primary" />
            Save Calculation
          </DialogTitle>
          <DialogDescription>
            Save this calculation and optionally assign it to a listing. 
            The calculation will appear on the listing's shareable page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="calc-name">Calculation Name</Label>
            <Input
              id="calc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Mortgage estimate for 123 Main St"
              data-testid="calculation-name-input"
            />
          </div>

          {/* Assign to Listing */}
          <div className="space-y-2">
            <Label>Assign to Listing (Optional)</Label>
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 size={16} className="animate-spin" />
                Loading listings...
              </div>
            ) : (
              <Select value={selectedListing} onValueChange={setSelectedListing}>
                <SelectTrigger data-testid="listing-select">
                  <SelectValue placeholder="Select a listing" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Don't assign to a listing</SelectItem>
                  {listings.map((listing) => (
                    <SelectItem key={listing.id} value={listing.id}>
                      <div className="flex items-center gap-2">
                        <Home size={14} />
                        <span className="truncate max-w-[250px]">{listing.address}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {listings.length === 0 && !isLoading && (
              <p className="text-xs text-muted-foreground">
                No active listings found. You can assign this calculation later.
              </p>
            )}
          </div>

          {/* Summary of what will be saved */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <p className="font-medium mb-1">
              {calculatorType === 'mortgage' ? 'Mortgage Calculation' : 'Affordability Calculation'}
            </p>
            {calculatorType === 'mortgage' && calculationData && (
              <p className="text-muted-foreground">
                ${calculationData.home_price?.toLocaleString()} home • 
                ${calculationData.monthly_payment?.toLocaleString()}/mo
              </p>
            )}
            {calculatorType === 'affordability' && calculationData && (
              <p className="text-muted-foreground">
                Max: ${calculationData.max_home_price?.toLocaleString()} • 
                ${calculationData.monthly_payment?.toLocaleString()}/mo
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving} data-testid="save-calculation-btn">
            {isSaving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Calculation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveCalculationDialog;
